import axios from 'axios';
import Cookies from 'js-cookie';
import { apiPath } from '../constants/defaultValues';
import { triggerSessionExpired, triggerNetworkError } from '../context/SessionContext';
import { refreshAccessToken } from './authService';
import { handleAxiosError } from './errorHandler';

const apiInstance = axios.create({
  baseURL: apiPath,
  headers: { 'Content-Type': 'application/json' },
});

apiInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ✅ Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          Cookies.set('access_token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return apiInstance(originalRequest);
        } else {
          processQueue(new Error('Refresh token failed'), null);
          triggerSessionExpired(); // open session expired
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.error('Token refresh failed:', refreshError);
        triggerSessionExpired(); // open session expired
      } finally {
        isRefreshing = false;
      }
    }

    // handle network errors like dns issues or server down
    if (!error.response) {
      console.error('Network error detected:', error.message);
      triggerNetworkError(); // open network error modal
    }

    // show toast message for any error except 401 (handled by Session Expired modal)
    if (error.response?.status !== 401) {
      handleAxiosError(error);
    }

    return Promise.reject(error);
  },
);

export default apiInstance;
